import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Save, X, ArrowLeft, Plus, Trash2, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import { useProtocolRisks } from '../../hooks/useRisks';
import { riskService } from '../../services/riskService';
import { useUserGroups } from '../../hooks/useUserGroups';
import { useActiveRoles } from '../../hooks/useRoles';
import { useAssignmentTypes, useShiftTypes } from '../../hooks/useTaskNomenclatures';
import { useEquipment, useSupplies, useDocumentationFieldTypes, useVerificationMethods } from '../../hooks/useNomenclatures';
import { userService } from '../../services/firestoreService';
import RiskSelectorModal from '../risks/RiskSelectorModal';
import Modal from '../ui/Modal';
import { useNotifications } from '../NotificationSystem';
import './ActionEditor.css';

const ActionEditor = ({ action, onSave, onCancel, roomTypes, triggerEvents, protocolId, protocolName, stepId, stepName }) => {
  const { t } = useTranslation('protocols');
  const { notify } = useNotifications();
  const [confirmRiskModal, setConfirmRiskModal] = useState(null);
  const { risks: protocolRisks, loading: risksLoading, refresh: refreshRisks } = useProtocolRisks(protocolId);
  
  // Personnel assignment hooks
  const { groups, loading: groupsLoading } = useUserGroups({ isActive: true });
  const { roles } = useActiveRoles();
  const { items: assignmentTypes } = useAssignmentTypes();
  const { items: shiftTypes } = useShiftTypes();
  const [users, setUsers] = useState([]);
  
  // Nomenclature hooks
  const { items: equipment } = useEquipment();
  const { items: supplies } = useSupplies();
  const { items: documentationFieldTypes } = useDocumentationFieldTypes();
  const { items: verificationMethods } = useVerificationMethods();
  
  // Load users for user-based assignment
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const usersData = await userService.getAll();
        setUsers(usersData || []);
      } catch (error) {
        console.error('Error loading users:', error);
        setUsers([]);
      }
    };
    loadUsers();
  }, []);

  // Auto-expand risks section when risks are present
  useEffect(() => {
    if (protocolRisks && protocolRisks.filter(r => r.level === 'action' && r.actionId === action.id).length > 0) {
      setRisksExpanded(true);
    }
  }, [protocolRisks, action.id]);
  
  const [formData, setFormData] = useState({
    ...action,
    name: action.name || '',
    description: action.description || '',
    order: action.order || 1,
    type: action.type || 'manual',
    estimatedDuration: action.estimatedDuration || 2,
    isRequired: action.isRequired !== undefined ? action.isRequired : true,
    equipment: action.equipment || [],
    supplies: action.supplies || [],
    conditions: [],
    documentationFields: action.documentationFields || [],
    verificationMethod: action.verificationMethod || '',
    notes: action.notes || '',
    // NEW: Task Template Assignment
    generateTask: action.generateTask || false,
    taskTemplateId: action.taskTemplateId || null,
    taskTemplateName: action.taskTemplateName || null,
    taskPriority: action.taskPriority || 'medium',
    taskUrgency: action.taskUrgency || 'routine',
    // NEW: Personnel Assignment (Action Level)
    assignmentRules: action.assignmentRules || {
      assignmentType: null,
      assignedToGroup: null,
      assignedToGroupName: null,
      requiredRoles: [],
      assignedToUser: null,
      assignedToUserName: null,
      shiftType: null,
      preferredShift: null,
      workloadThreshold: 5,
      assignmentNotes: ''
    }
  });

  const [errors, setErrors] = useState({});
  
  // Risk management state
  const [showRiskSelector, setShowRiskSelector] = useState(false);
  const [risksExpanded, setRisksExpanded] = useState(false);

  // Enhanced validation
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = t('actionEditor.actionNameRequiredVal');
    } else if (formData.name.trim().length < 3) {
      newErrors.name = t('actionEditor.actionNameMinLength');
    } else if (formData.name.trim().length > 200) {
      newErrors.name = t('actionEditor.actionNameMaxLength');
    }
    if (!formData.description.trim()) {
      newErrors.description = t('actionEditor.actionDescriptionRequiredVal');
    } else if (formData.description.trim().length < 10) {
      newErrors.description = t('actionEditor.actionDescriptionMinLengthVal');
    } else if (formData.description.trim().length > 2000) {
      newErrors.description = t('actionEditor.actionDescriptionMaxLength');
    }
    if (!formData.order || formData.order < 1) {
      newErrors.order = t('stepEditor.orderMinVal');
    } else if (formData.order > 999) {
      newErrors.order = t('stepEditor.orderMaxVal');
    }
    if (!formData.estimatedDuration || formData.estimatedDuration <= 0) {
      newErrors.estimatedDuration = t('stepEditor.durationMinVal');
    } else if (formData.estimatedDuration > 1440) {
      newErrors.estimatedDuration = t('stepEditor.durationMaxVal');
    }
    if (formData.equipment && formData.equipment.length > 0) {
      const equipmentNames = new Set();
      formData.equipment.forEach((equipment, index) => {
        const equipmentName = typeof equipment === 'string' ? equipment : equipment.name;
        const n = index + 1;
        if (!equipmentName || !equipmentName.trim()) {
          newErrors[`equipment_${index}`] = t('actionEditor.equipmentItemEmpty', { index: n });
        } else if (equipmentNames.has(equipmentName.trim().toLowerCase())) {
          newErrors[`equipment_${index}`] = t('actionEditor.equipmentItemUnique', { index: n });
        } else {
          equipmentNames.add(equipmentName.trim().toLowerCase());
        }
      });
    }
    if (formData.supplies && formData.supplies.length > 0) {
      const supplyNames = new Set();
      formData.supplies.forEach((supply, index) => {
        const supplyName = typeof supply === 'string' ? supply : supply.name;
        const n = index + 1;
        if (!supplyName || !supplyName.trim()) {
          newErrors[`supply_${index}`] = t('actionEditor.supplyItemEmpty', { index: n });
        } else if (supplyNames.has(supplyName.trim().toLowerCase())) {
          newErrors[`supply_${index}`] = t('actionEditor.supplyItemUnique', { index: n });
        } else {
          supplyNames.add(supplyName.trim().toLowerCase());
        }
      });
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Form handlers
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  // Assignment rules handlers
  const handleAssignmentChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      assignmentRules: {
        ...prev.assignmentRules,
        [field]: value
      }
    }));
  };

  // Equipment management - use multi-select directly
  const handleEquipmentChange = (selectedValues) => {
    const selectedEquipmentNames = Array.from(selectedValues).map(id => {
      const item = equipment.find(eq => eq.id === id);
      return item ? item.name : null;
    }).filter(Boolean);
    
      setFormData(prev => ({
        ...prev,
      equipment: selectedEquipmentNames.map(name => ({
        id: Date.now().toString() + '-' + name,
        name,
          isRequired: true
      }))
    }));
  };

  // Supplies management - use multi-select directly
  const handleSuppliesChange = (selectedValues) => {
    const selectedSupplyNames = Array.from(selectedValues).map(id => {
      const item = supplies.find(sup => sup.id === id);
      return item ? item.name : null;
    }).filter(Boolean);
    
    setFormData(prev => ({
      ...prev,
      supplies: selectedSupplyNames.map(name => ({
        id: Date.now().toString() + '-' + name,
        name,
          quantity: 1,
          isRequired: true
      }))
    }));
  };

  // Conditions and Documentation Fields now handled directly in onChange handlers

  // Risk management handlers
  const handleAddActionRisk = () => {
    setShowRiskSelector(true);
  };

  const handleRiskSelected = (association) => {
    setShowRiskSelector(false);
    setRisksExpanded(true); // Auto-expand when risk is added
    refreshRisks();
    console.log('✅ Risk associated to action successfully');
  };

  const handleRemoveRisk = (riskId, riskScenario) => {
    setConfirmRiskModal({ id: riskId, scenario: riskScenario });
  };

  const handleConfirmRemoveRisk = async () => {
    if (!confirmRiskModal) return;
    try {
      await riskService.removeFromProtocol(confirmRiskModal.id);
      refreshRisks();
      setConfirmRiskModal(null);
    } catch (error) {
      console.error('Error removing risk:', error);
      notify.error(t('stepEditor.failedToRemoveRisk'));
    }
  };

  // Save action
  const handleSave = () => {
    if (!validateForm()) {
      console.log('❌ Action validation failed');
      return;
    }
    console.log('✅ Action validation passed, saving...');
    onSave({ ...formData, conditions: [] });
  };

  return (
    <div className="action-editor" style={{ width: '100%', maxWidth: 'none', margin: 0 }}>
      <div className="action-editor-header">
        <button
          type="button"
          onClick={onCancel}
          className="btn btn-secondary"
        >
          <ArrowLeft size={16} />
          {t('actionEditor.backToStep')}
        </button>
        
        <h3>{t('actionEditor.editActionNumber', { number: action.actionNumber })}</h3>
        
        <div className="action-editor-actions">
          <button 
            type="button" 
            onClick={onCancel}
            className="btn btn-secondary"
          >
            <X size={16} />
            {t('cancel')}
          </button>
          <button 
            type="button" 
            onClick={handleSave}
            className="btn btn-primary"
          >
            <Save size={16} />
            {t('actionEditor.saveAction')}
          </button>
        </div>
      </div>

      <div className="action-editor-content">
        <div className="form-section">
          <h4>{t('actionEditor.actionInformation')}</h4>
          <div className="action-info-grid">
            <div className="form-group full-width">
              <label htmlFor="actionName">{t('actionEditor.actionNameRequired')}</label>
              <input
                type="text"
                id="actionName"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className={errors.name ? 'error' : ''}
                placeholder={t('actionEditor.enterActionName')}
              />
              {errors.name && <span className="error-text">{errors.name}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="isRequired">{t('stepEditor.required')}</label>
              <select
                id="isRequired"
                value={formData.isRequired ? 'true' : 'false'}
                onChange={(e) => handleInputChange('isRequired', e.target.value === 'true')}
              >
                <option value="true">{t('stepEditor.required')}</option>
                <option value="false">{t('stepEditor.optional')}</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="actionOrder">{t('stepEditor.orderRequired')}</label>
              <input
                type="number"
                id="actionOrder"
                value={formData.order}
                onChange={(e) => handleInputChange('order', parseInt(e.target.value) || 1)}
                className={errors.order ? 'error' : ''}
                min="1"
                placeholder="1"
              />
              {errors.order && <span className="error-text">{errors.order}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="actionDuration">{t('actionEditor.estimatedDurationMinutes')}</label>
              <input
                type="number"
                id="actionDuration"
                value={formData.estimatedDuration}
                onChange={(e) => handleInputChange('estimatedDuration', parseInt(e.target.value) || 0)}
                className={errors.estimatedDuration ? 'error' : ''}
                min="1"
                placeholder="2"
              />
              {errors.estimatedDuration && <span className="error-text">{errors.estimatedDuration}</span>}
            </div>

            <div className="form-group full-width">
              <label htmlFor="actionDescription">{t('stepEditor.descriptionRequired')}</label>
              <textarea
                id="actionDescription"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                className={errors.description ? 'error' : ''}
                rows={3}
                placeholder={t('actionEditor.actionDescriptionPlaceholder')}
              />
              {errors.description && <span className="error-text">{errors.description}</span>}
            </div>

            <div className="form-group full-width">
              <label htmlFor="actionNotes">{t('actionEditor.notes')}</label>
              <textarea
                id="actionNotes"
                value={formData.notes || ''}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                rows={3}
                placeholder={t('actionEditor.notesPlaceholder')}
              />
            </div>

            <div className="form-group">
              <label htmlFor="verificationMethod">{t('stepEditor.verificationMethod')}</label>
              <select
                id="verificationMethod"
                value={formData.verificationMethod}
                onChange={(e) => handleInputChange('verificationMethod', e.target.value)}
              >
                <option value="">{t('stepEditor.selectVerificationMethod')}</option>
                {verificationMethods.map(method => (
                  <option key={method.id} value={method.name || method.code}>
                    {method.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h4>{t('stepEditor.taskGenerationOptional')}</h4>
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.generateTask}
                onChange={(e) => handleInputChange('generateTask', e.target.checked)}
                style={{ marginRight: '8px' }}
              />
              {t('actionEditor.generateTaskFromAction')}
            </label>
            <small style={{ display: 'block', marginTop: '4px', color: '#6b7280', fontSize: '0.75rem' }}>
              {t('actionEditor.generateTaskFromActionHint')}
            </small>
          </div>

          {formData.generateTask && (
            <>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="taskPriority">{t('stepEditor.taskPriority')}</label>
                  <select
                    id="taskPriority"
                    value={formData.taskPriority}
                    onChange={(e) => handleInputChange('taskPriority', e.target.value)}
                  >
                    <option value="low">{t('stepEditor.low')}</option>
                    <option value="medium">{t('stepEditor.medium')}</option>
                    <option value="high">{t('stepEditor.high')}</option>
                    <option value="critical">{t('stepEditor.critical')}</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="taskUrgency">{t('stepEditor.taskUrgency')}</label>
                  <select
                    id="taskUrgency"
                    value={formData.taskUrgency}
                    onChange={(e) => handleInputChange('taskUrgency', e.target.value)}
                  >
                    <option value="routine">{t('stepEditor.routine')}</option>
                    <option value="urgent">{t('stepEditor.urgent')}</option>
                    <option value="emergency">{t('stepEditor.emergency')}</option>
                  </select>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="form-section">
          <h4>{t('actionEditor.personnelAssignmentAction')}</h4>
          <p className="section-description" style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem' }}>
            {t('actionEditor.personnelAssignmentActionHint')}
          </p>
          <div className="form-grid">
            <div className="form-group">
              <label>{t('stepEditor.assignmentType')}</label>
              <select
                value={formData.assignmentRules.assignmentType || ''}
                onChange={(e) => {
                  const assignmentType = e.target.value || null;
                  handleAssignmentChange('assignmentType', assignmentType);
                  if (assignmentType !== formData.assignmentRules.assignmentType) {
                    handleAssignmentChange('assignedToGroup', null);
                    handleAssignmentChange('assignedToGroupName', null);
                    handleAssignmentChange('requiredRoles', []);
                    handleAssignmentChange('assignedToUser', null);
                    handleAssignmentChange('assignedToUserName', null);
                    handleAssignmentChange('shiftType', null);
                  }
                }}
              >
                <option value="">{t('actionEditor.useStepProtocolAssignment')}</option>
                {assignmentTypes && assignmentTypes.map(type => (
                  <option key={type.id} value={type.id}>{type.name}</option>
                ))}
              </select>
              <small style={{ display: 'block', marginTop: '4px', color: '#6b7280', fontSize: '0.75rem' }}>
                {t('actionEditor.assignmentActionHint')}
              </small>
            </div>

            {(() => {
              if (!formData.assignmentRules.assignmentType) return null;
              const selectedType = assignmentTypes?.find(ty => ty.id === formData.assignmentRules.assignmentType);
              const typeName = selectedType?.name?.toLowerCase() || '';
              if (typeName.includes('group')) {
                return (
                  <div className="form-group" key="group-assignment">
                    <label>{t('stepEditor.assignedToGroup')}</label>
                    <select
                      value={formData.assignmentRules.assignedToGroup || ''}
                      onChange={(e) => {
                        const groupId = e.target.value || null;
                        const group = groups.find(g => g.id === groupId);
                        handleAssignmentChange('assignedToGroup', groupId);
                        handleAssignmentChange('assignedToGroupName', group?.name || null);
                      }}
                      required
                    >
                      <option value="">{t('stepEditor.selectGroup')}</option>
                      {groupsLoading ? (
                        <option disabled>{t('stepEditor.loadingGroups')}</option>
                      ) : groups && groups.length > 0 ? (
                        groups.map(group => (
                          <option key={group.id} value={group.id}>{group.name || group.id}</option>
                        ))
                      ) : (
                        <option disabled>{t('stepEditor.noGroupsAvailable')}</option>
                      )}
                    </select>
                  </div>
                );
              }
              if (typeName.includes('role')) {
                return (
                  <div className="form-group" key="role-assignment">
                    <label>{t('stepEditor.requiredRoles')}</label>
                    <select
                      multiple
                      value={formData.assignmentRules.requiredRoles || []}
                      onChange={(e) => {
                        const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
                        handleAssignmentChange('requiredRoles', selectedOptions);
                      }}
                      size={Math.min(roles?.length || 3, 6)}
                      style={{ height: 'auto', minHeight: '100px' }}
                      required
                    >
                      {roles && roles.length > 0 ? (
                        roles.map(role => (
                          <option key={role.id} value={role.id}>
                            {role.name || role.role_id}
                          </option>
                        ))
                      ) : (
                        <option disabled>{t('stepEditor.noRolesAvailable')}</option>
                      )}
                    </select>
                    <small style={{ display: 'block', marginTop: '4px', color: '#6b7280', fontSize: '0.75rem' }}>
                      {t('stepEditor.holdCtrlToSelectRoles')}
                    </small>
                  </div>
                );
              }
              if (typeName.includes('shift')) {
                return (
                  <div className="form-group" key="shift-assignment">
                    <label>{t('stepEditor.shiftType')}</label>
                    <select
                      value={formData.assignmentRules.shiftType || ''}
                      onChange={(e) => handleAssignmentChange('shiftType', e.target.value || null)}
                      required
                    >
                      <option value="">{t('stepEditor.selectShift')}</option>
                      {shiftTypes && shiftTypes.map(shift => (
                        <option key={shift.id} value={shift.id}>{shift.name}</option>
                      ))}
                    </select>
                  </div>
                );
              }
              if (typeName.includes('user') || typeName.includes('direct')) {
                return (
                  <div className="form-group" key="user-direct-assignment">
                    <label>{typeName.includes('direct') ? t('stepEditor.assignToUserOptional') : t('stepEditor.assignToUser')}</label>
                    <select
                      value={formData.assignmentRules.assignedToUser || ''}
                      onChange={(e) => {
                        const userId = e.target.value || null;
                        const user = users.find(u => u.id === userId);
                        handleAssignmentChange('assignedToUser', userId);
                        handleAssignmentChange('assignedToUserName', user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email : null);
                      }}
                      required={!typeName.includes('direct')}
                    >
                      <option value="">{t('stepEditor.selectUser')}</option>
                      {users.map(user => (
                        <option key={user.id} value={user.id}>
                          {user.first_name && user.last_name 
                            ? `${user.first_name} ${user.last_name} (${user.email || user.id})`
                            : user.email || user.id}
                        </option>
                      ))}
                    </select>
                  </div>
                );
              }
              
              return null;
            })()}
          </div>
        </div>

        <div className="form-section">
          <h4>{t('actionEditor.equipment')}</h4>
          <div className="form-group">
            <label htmlFor="equipment">{t('actionEditor.requiredEquipment')}</label>
            <select
              id="equipment"
              multiple
              size={Math.min(equipment.length, 5)}
              value={formData.equipment.map(eq => {
                const item = equipment.find(e => e.name === eq.name);
                return item ? item.id : '';
              }).filter(Boolean)}
              onChange={(e) => {
                const selected = Array.from(e.target.selectedOptions, option => option.value);
                handleEquipmentChange(selected);
              }}
              className="form-select-multi"
            >
              {equipment.map(eq => (
                <option key={eq.id} value={eq.id}>
                  {eq.name}
                </option>
              ))}
            </select>
            <small>{t('actionEditor.holdCtrlEquipment')}</small>
          </div>
        </div>

        <div className="form-section">
          <h4>{t('actionEditor.supplies')}</h4>
          <div className="form-group">
            <label htmlFor="supplies">{t('actionEditor.requiredSupplies')}</label>
            <select
              id="supplies"
              multiple
              size={Math.min(supplies.length, 5)}
              value={formData.supplies.map(sup => {
                const item = supplies.find(s => s.name === sup.name);
                return item ? item.id : '';
              }).filter(Boolean)}
              onChange={(e) => {
                const selected = Array.from(e.target.selectedOptions, option => option.value);
                handleSuppliesChange(selected);
              }}
              className="form-select-multi"
            >
              {supplies.map(sup => (
                <option key={sup.id} value={sup.id}>
                  {sup.name}
                </option>
              ))}
            </select>
            <small>{t('actionEditor.holdCtrlSupplies')}</small>
          </div>
        </div>

        <div className="form-section">
          <h4>{t('actionEditor.documentationFields')}</h4>
          <div className="form-group">
            <label htmlFor="documentationFields">{t('actionEditor.documentationFieldTypes')}</label>
                      <select
              id="documentationFields"
              multiple
              size={Math.min(documentationFieldTypes.length, 5)}
              value={formData.documentationFields.map(field => {
                const item = documentationFieldTypes.find(ft => ft.id === field.documentationFieldTypeId || ft.code === field.fieldType || ft.name.toLowerCase() === field.fieldType?.toLowerCase());
                return item ? item.id : '';
              }).filter(Boolean)}
              onChange={(e) => {
                const selected = Array.from(e.target.selectedOptions, option => option.value);
                const selectedFieldTypes = selected.map(id => {
                  const item = documentationFieldTypes.find(ft => ft.id === id);
                  return item ? {
                    id: Date.now().toString() + '-' + id,
                    documentationFieldTypeId: id,
                    fieldType: item.code || item.name.toLowerCase(),
                    fieldName: item.name,
                    isRequired: false
                  } : null;
                }).filter(Boolean);
                setFormData(prev => ({
                  ...prev,
                  documentationFields: selectedFieldTypes
                }));
              }}
              className="form-select-multi"
            >
              {documentationFieldTypes.map(ft => (
                <option key={ft.id} value={ft.id}>
                  {ft.name}
                </option>
              ))}
                      </select>
            <small>{t('actionEditor.holdCtrlDocumentation')}</small>
          </div>
        </div>

        {protocolId && action.id && (
          <div className="form-section">
            <div 
              className="section-header-collapsible"
              onClick={() => setRisksExpanded(!risksExpanded)}
            >
              <h4>
                <AlertTriangle size={18} />
                {t('actionEditor.associatedRisksAction')}
                {protocolRisks && protocolRisks.filter(r => r.level === 'action' && r.actionId === action.id).length > 0 && (
                  <span className="risk-count-badge">
                    {protocolRisks.filter(r => r.level === 'action' && r.actionId === action.id).length}
                  </span>
                )}
              </h4>
              <div className="section-header-actions">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAddActionRisk();
                  }}
                  className="btn btn-success section-add-btn"
                >
                  <Plus size={16} />
                  {t('stepEditor.addRisk')}
                </button>
              {risksExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </div>
            </div>

            {risksExpanded && (
              <div className="risks-section-content">
                {risksLoading ? (
                  <div className="loading-state-small">
                    <div className="spinner-small"></div>
                    <span>{t('stepEditor.loadingRisks')}</span>
                  </div>
                ) : protocolRisks && protocolRisks.filter(r => r.level === 'action' && r.actionId === action.id).length > 0 ? (
                  <div className="risks-list">
                    {protocolRisks
                      .filter(r => r.level === 'action' && r.actionId === action.id)
                      .map(risk => (
                        <div key={risk.id} className="risk-item-compact">
                          <div className="risk-item-info">
                            <strong>{risk.riskScenario}</strong>
                            <div className="risk-item-meta">
                              <span className="rpn-badge-small">RPN: {risk.rpn}</span>
                              <span className={`criticality-badge-small criticality-${risk.criticality?.toLowerCase()}`}>
                                {risk.criticality}
                              </span>
                              {risk.riskVersion && (
                                <span className="version-badge-small">v{risk.riskVersion}</span>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => handleRemoveRisk(risk.id, risk.riskScenario)}
                            className="btn btn-delete"
                            title={t('stepEditor.removeRiskAssociation')}
                          >
                            <Trash2 size={14} />
                            {t('protocols:commonRemove')}
                          </button>
                        </div>
                      ))}
                  </div>
                ) : (
                  <p className="no-risks-message">
                    {t('actionEditor.noActionRisksYet')}
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {confirmRiskModal && (
          <Modal
            isOpen={!!confirmRiskModal}
            onClose={() => setConfirmRiskModal(null)}
            title={t('stepEditor.removeRiskAssociation')}
          >
            <p>{t('stepEditor.confirmRemoveRisk', { scenario: confirmRiskModal.scenario })}</p>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button type="button" className="btn btn-secondary" onClick={() => setConfirmRiskModal(null)}>
                {t('cancel')}
              </button>
              <button type="button" className="btn btn-primary" onClick={handleConfirmRemoveRisk}>
                {t('protocols:commonRemove')}
              </button>
            </div>
          </Modal>
        )}

        {/* Risk Selector Modal */}
        {showRiskSelector && protocolId && action.id && (
          <RiskSelectorModal
            protocolId={protocolId}
            protocolName={protocolName}
            level="action"
            stepId={stepId}
            stepName={stepName}
            actionId={action.id}
            actionName={formData.name}
            onSelect={handleRiskSelected}
            onClose={() => setShowRiskSelector(false)}
          />
        )}
      </div>
    </div>
  );
};

export default ActionEditor;
